import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { URLBuilder } from 'url-builder/url-builder';

import ScrollToTop from 'lib/scroll/scroll-to-top';
import Styled from 'styled';
import FetchData from 'lib/fetch-data/fetch-data';
import LoadingProgress from 'lib/react-router/loading-progress';
import { Halfpage, ZoznamHalfpageBottom, ZoznamHalfpageTop } from 'lib/google/dfp/ad-units/halfpage';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import PrArticles from 'four-nets/widgets/pr-articles/pr-articles';
import { CategoryStore } from 'four-nets/forum/forum';
import NewThreadView, { NewThreadViewModel } from 'four-nets/forum/thread/new-thread/new-thread-view';
import ForumWidgetView, { ForumWidgetViewModel } from 'four-nets/forum/forum-widget/forum-widget-view';
import { UserStore } from 'four-nets/user/user';
import ErrorPage from 'four-nets/errors/error-page';
import SearchPanel from 'four-nets/search/search-panel/search-panel';
import { Site } from 'four-nets/site';
import Contest from 'four-nets/contest/contest';
import HotContentWidget from 'four-nets/cms/hot-content-widget';
import PlaceholderPanel from './placeholder-panel/placeholder-panel';

import PanelView, { PanelViewModel } from './panel-view';

import defaultStyles from './categories-loader.scss';

@Styled(defaultStyles)
@observer
class CategoriesLoader extends React.Component {
    static contextTypes = {
        api: PropTypes.object,
        app: PropTypes.object,
        categoryStore: PropTypes.instanceOf(CategoryStore),
        site: PropTypes.instanceOf(Site),
        urlBuilder: PropTypes.instanceOf(URLBuilder),
        userStore: PropTypes.instanceOf(UserStore),
        authCheck: PropTypes.func,
    };

    panelViewModel;
    newThreadViewModel;
    forumWidgetViewModel;
    newThreadView;

    constructor(props, context) {
        super(props, context);
        const { categoryStore, site } = context;

        categoryStore.site = site;

        this.panelViewModel = new PanelViewModel({
            categoryStore,
        });

        this.newThreadViewModel = new NewThreadViewModel({
            categoryStore,
            userStore: categoryStore.userStore,
        });

        this.forumWidgetViewModel = new ForumWidgetViewModel({ categoryStore });
    }

    loadDidSucceed = ([mostRecent, mostActive]) => {
        const { categoryStore } = this.context;

        categoryStore.threadStore.mostRecent = mostRecent;
        categoryStore.threadStore.mostActive = mostActive;

        return [mostRecent, mostActive];
    };

    transform = data => {
        const { categoryStore } = this.context;

        categoryStore.fromJSON({ json: data.categoryStore });

        const [mostRecent, mostActive] = this.loadDidSucceed([
            categoryStore.threadStore.processPagedJSON({ json: data.mostRecent }),
            categoryStore.threadStore.processPagedJSON({ json: data.mostActive }),
        ]);

        this.forumWidgetViewModel.fromJSON(data.forumWidgetViewModel);

        return {
            categoryStore,
            mostRecent,
            mostActive,
            forumWidgetViewModel: this.forumWidgetViewModel,
        };
    };

    fetch = setData => {
        const { categoryStore } = this.context;
        const limit = 3;

        return categoryStore
            .loadAll()
            .then(() =>
                Promise.all([
                    categoryStore.threadStore.loadMostRecent({ limit }),
                    categoryStore.threadStore.loadMostActive({ limit }),
                    this.forumWidgetViewModel.load(),
                ])
            )
            .then(this.loadDidSucceed)
            .then(() => {
                setData({
                    categoryStore,
                    forumWidgetViewModel: this.forumWidgetViewModel,
                    mostRecent: categoryStore.threadStore.mostRecent,
                    mostActive: categoryStore.threadStore.mostActive,
                });
            });
    };

    setNewThreadView = ref => {
        this.newThreadView = ref;
    };

    onNewThreadClick = () => {
        const { newThreadView } = this;

        if (newThreadView) {
            const action = () => {
                newThreadView.show({
                    form: this.newThreadViewModel.setupForm(),
                });
                return Promise.resolve();
            };

            this.context.authCheck(action, AuthOrigin.NewThread);
        }
    };

    renderFetch = props => {
        const { loading, error, data } = props;

        const { styles } = this.props;
        const { site } = this.context;

        const { panelViewModel, newThreadViewModel } = this;

        return (
            <LoadingProgress loading={loading}>
                <ErrorPage error={error}>
                    <div className={styles.categories}>
                        <div className={styles.splitDiv}>
                            <div className={styles.splitLeft}>
                                <SearchPanel
                                    style={{ searchPanel: styles.searchPanel, form: styles.form }}
                                    history={this.props.history}
                                    target='forum'
                                    submitOnToggle={false}
                                />
                                {!panelViewModel?.columns ? <PlaceholderPanel /> : null}
                                <PanelView
                                    title={<FormattedMessage id='FORUM_CATEGORIES_LONG' />}
                                    panelViewModel={panelViewModel}
                                    onNewThreadClick={this.onNewThreadClick}
                                />
                            </div>
                            <div className={styles.splitRight}>
                                <Contest />
                                <HotContentWidget />
                                <ForumWidgetView
                                    hideHeader={true}
                                    forumWidgetViewModel={data?.forumWidgetViewModel || this.forumWidgetViewModel}
                                />
                                {site.isSlovak && site.isWedding ? (
                                    <ZoznamHalfpageTop />
                                ) : (
                                    <Halfpage adUnitCode='halfpage-top' />
                                )}
                                <PrArticles />
                                {site.isSlovak && site.isWedding ? (
                                    <ZoznamHalfpageBottom />
                                ) : (
                                    <Halfpage adUnitCode='halfpage-bottom' />
                                )}
                            </div>
                        </div>
                        <NewThreadView innerRef={this.setNewThreadView} newThreadViewModel={newThreadViewModel} />
                    </div>
                    {!loading && !error ? <ScrollToTop /> : null}
                </ErrorPage>
            </LoadingProgress>
        );
    };

    render() {
        return (
            <FetchData
                serializerKey={__filename}
                fetch={this.fetch}
                render={this.renderFetch}
                transform={this.transform}
            />
        );
    }
}

export default CategoriesLoader;
